import { SentenceBuffer } from './sentence-buffer';
import type { BaseStreamEvent } from './chat-api.service';
import type { Citation, SuggestedCTA } from './types';
import type { VoiceModeCallbacks } from './voice-mode-orchestrator';

/** Simple async queue: producer pushes items, consumer awaits via for-await. */
export class SentenceQueue {
  private items: string[] = [];
  private done = false;
  private notify: (() => void) | null = null;

  push(item: string): void {
    this.items.push(item);
    this.notify?.();
    this.notify = null;
  }

  finish(): void {
    this.done = true;
    this.notify?.();
    this.notify = null;
  }

  async *[Symbol.asyncIterator](): AsyncGenerator<string> {
    while (true) {
      if (this.items.length > 0) {
        yield this.items.shift()!;
      } else if (this.done) {
        break;
      } else {
        await new Promise<void>((r) => {
          this.notify = r;
        });
      }
    }
  }
}

interface StreamMetadata {
  citations?: Citation[];
  suggestedCTA?: SuggestedCTA | null;
}

export interface StreamHandlers {
  onChunk: (chunk: string) => void;
  onSentence: (sentence: string) => void;
  onMetadata: (metadata: StreamMetadata) => void;
  onStreamEnd: () => void;
  shouldStop?: () => boolean;
}

/** Consumes an SSE voice stream, parsing text into sentences via
 *  SentenceBuffer and dispatching events through handlers. Shared by
 *  both the speculative pipeline and the fresh streaming path. */
export async function consumeEventStream(
  stream: AsyncGenerator<BaseStreamEvent>,
  handlers: StreamHandlers
): Promise<void> {
  const sentenceBuffer = new SentenceBuffer();

  try {
    for await (const event of stream) {
      if (handlers.shouldStop?.()) return;

      if (event.type === 'text') {
        handlers.onChunk(event.chunk);
        const stripped = event.chunk.replace(/\[\d+\]/g, '');
        for (const sentence of sentenceBuffer.push(stripped)) {
          handlers.onSentence(sentence);
        }
      } else if (event.type === 'metadata') {
        handlers.onMetadata({
          citations: event.citations,
          suggestedCTA: event.suggestedCTA,
        });
      }
    }

    const remaining = sentenceBuffer.flush();
    if (remaining) {
      handlers.onSentence(remaining);
    }
  } finally {
    handlers.onStreamEnd();
  }
}

/** Internal state for a speculative consumption run. The `committed` flag
 *  switches sentence routing from direct TTS (speculative) to SentenceQueue
 *  (committed playback). */
interface SpeculativePipeline {
  chunks: string[];
  metadata: StreamMetadata | null;
  audioPromises: Promise<Response>[];
  sentenceQueue: SentenceQueue;
  committed: boolean;
  consumptionPromise: Promise<void>;
}

/** Returned by `take()` when a speculative pipeline matches the transcript.
 *  Contains everything the orchestrator needs to start playback. */
export interface CommittedPipeline {
  displayText: string;
  metadata: StreamMetadata | null;
  audioSequence: AsyncGenerator<() => Promise<Response>>;
  displayPromise: Promise<void>;
}

/** Manages speculative stream consumption and TTS pre-fetching. During the
 *  speculative window (500-1500ms of silence), consumes the SSE stream and
 *  fires TTS requests eagerly. At commit time, hands off pre-fetched audio
 *  and continues routing new sentences through a SentenceQueue. */
export class SpeculativePipelineManager {
  private abortController: AbortController | null = null;
  private pipeline: SpeculativePipeline | null = null;
  private transcript: string | null = null;

  /** Starts a speculative pipeline: fetches the voice stream, consumes SSE
   *  events, and fires TTS for each sentence before we've committed. */
  start(cb: VoiceModeCallbacks, transcript: string): void {
    this.abort();

    const controller = new AbortController();
    this.abortController = controller;
    this.transcript = transcript;

    const pipeline: SpeculativePipeline = {
      chunks: [],
      metadata: null,
      audioPromises: [],
      sentenceQueue: new SentenceQueue(),
      committed: false,
      consumptionPromise: Promise.resolve(),
    };

    pipeline.consumptionPromise = this.runConsumption(
      cb,
      transcript,
      pipeline,
      controller.signal
    ).catch(() => {});

    this.pipeline = pipeline;
  }

  /** Returns a CommittedPipeline if the transcript matches, otherwise aborts
   *  and returns null (caller should fall back to a fresh request). */
  take(cb: VoiceModeCallbacks, transcript: string): CommittedPipeline | null {
    if (this.pipeline && this.transcript === transcript) {
      const pipeline = this.pipeline;
      this.pipeline = null;
      this.transcript = null;
      // Keep AbortController alive - the stream still needs it
      this.abortController = null;

      pipeline.committed = true;

      const displayText = pipeline.chunks.join('').replace(/\[\d+\]/g, '');

      return {
        displayText: displayText ? `${displayText} ...` : '',
        metadata: pipeline.metadata,
        audioSequence: this.buildAudioSequence(cb, pipeline),
        displayPromise: this.waitForDisplay(cb, pipeline),
      };
    }

    this.abort();
    return null;
  }

  /** Aborts any in-flight speculative pipeline. */
  abort(): void {
    if (this.abortController) {
      this.abortController.abort();
    }
    if (this.pipeline) {
      this.pipeline.sentenceQueue.finish();
    }
    this.abortController = null;
    this.pipeline = null;
    this.transcript = null;
  }

  // -- Private --

  private async runConsumption(
    cb: VoiceModeCallbacks,
    transcript: string,
    pipeline: SpeculativePipeline,
    signal: AbortSignal
  ): Promise<void> {
    const response = await cb.chat.startSpeculativeFetch(transcript, signal);
    if (signal.aborted) return;

    const stream = cb.chat.consumeVoiceStream(response);

    await consumeEventStream(stream, {
      onChunk: (chunk) => {
        pipeline.chunks.push(chunk);
        if (pipeline.committed) {
          cb.chat.updateVoiceAnswer(
            `${pipeline.chunks.join('').replace(/\[\d+\]/g, '')} ...`
          );
        }
      },
      onSentence: (sentence) => {
        if (!pipeline.committed) {
          pipeline.audioPromises.push(cb.tts.requestAudio(sentence));
        } else {
          pipeline.sentenceQueue.push(sentence);
        }
      },
      onMetadata: (metadata) => {
        pipeline.metadata = metadata;
        if (pipeline.committed) {
          cb.chat.updateVoiceMetadata(metadata);
        }
      },
      onStreamEnd: () => {
        pipeline.sentenceQueue.finish();
      },
      shouldStop: () => signal.aborted,
    });
  }

  /** Yields pre-fetched TTS responses first (may already be resolved),
   *  then drains the ongoing queue for sentences arriving after commit. */
  private async *buildAudioSequence(
    cb: VoiceModeCallbacks,
    pipeline: SpeculativePipeline
  ): AsyncGenerator<() => Promise<Response>> {
    for (const promise of pipeline.audioPromises) {
      yield () => promise;
    }
    for await (const sentence of pipeline.sentenceQueue) {
      yield () => cb.tts.requestAudio(sentence);
    }
  }

  /** Waits for stream consumption to finish, then updates display with
   *  the final text including citation markers for styled rendering. */
  private async waitForDisplay(
    cb: VoiceModeCallbacks,
    pipeline: SpeculativePipeline
  ): Promise<void> {
    await pipeline.consumptionPromise;

    // Restore full text with [1] markers now that the stream is complete.
    // AssistantMessage needs markers in the text AND citations in state
    // to render styled citations.
    const fullAnswer = pipeline.chunks.join('');
    if (fullAnswer) {
      cb.chat.updateVoiceAnswer(fullAnswer);
    }
  }
}
